import {
    type CSSProperties,
    type FC,
    type ReactNode,
    useContext,
} from "react";
import { createStyleFromPadding } from "../../PaddingInput";
import { createBorderStyles } from "../BorderSettingsDialog";
import { DragDropComponentEmptyContext } from "../DragDropComponent";
import type { ComponentOptions, DragDropTableColumnType } from "../DragDropTypes";

interface ListColumnDefinition {
    labelStyle?: CSSProperties;
    valueStyle?: CSSProperties;
    format?: (data: unknown) => ReactNode;
}

export type ListColumnMap = Record<string, ListColumnDefinition>;

interface Props extends ComponentOptions {
    columns: ListColumnMap;
    data: Record<string, unknown>;
    value: Array<DragDropTableColumnType>;
}

export const ListComponent: FC<Props> = ({
    columns,
    style,
    value,
    data,
}) => {
    const { border, padding, fontSize, ...styleRest } = style;

    const empty = useContext(DragDropComponentEmptyContext);

    const tableStyle: CSSProperties = {
        tableLayout: "auto",
        width      : "100%",
        fontSize   : fontSize ? `${fontSize}px` : undefined,
        ...styleRest,
        ...(typeof padding === "undefined" ? {} : createStyleFromPadding(padding)),
        ...(typeof border === "undefined" ? {} : createBorderStyles(border)),
    };

    const tdStyle: CSSProperties = {
        border       : "none",
        paddingTop   : 0,
        paddingBottom: 0,
        textAlign    : "right",
    };

    const validColumns: Array<DragDropTableColumnType> = [];

    for(let i = 0, l = value.length; i < l; i++) {
        const column = value[i];

        if(!column.enabled) {
            continue;
        }

        if(!(column.column in columns)) {
            continue;
        }

        validColumns.push(column);
    }

    if(!validColumns.length) {
        return empty;
    }

    return (
        <table style={tableStyle}>
            <tbody>
                {validColumns.map(column => {
                    const datum = data[column.column];

                    if(!datum) {
                        return null;
                    }

                    const { labelStyle, valueStyle, format } = columns[column.column as any];

                    return (
                        <tr key={column.column}>
                            <td
                                style={{
                                    ...tdStyle,
                                    paddingLeft: "0",
                                    ...(labelStyle ? labelStyle : {}),
                                }}
                            >
                                {column.name}
                            </td>
                            <td
                                style={{
                                    ...tdStyle,
                                    paddingRight: "0",
                                    ...(valueStyle ? valueStyle : {}),
                                }}
                            >
                                {
                                    typeof format === "function" ?
                                        format(datum) :
                                        datum as string
                                }
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};
